/**
 * Security Settings Page (Story 1.7)
 * Displays 2FA, password change, and related controls.
 */
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../store/authStore";
import { profileService } from "../../services/profileService";
import { getApiErrorMessage } from "../../utils/apiError";
import { validatePasswordPolicy } from "../../utils/passwordPolicy";
import { useTwoFactorStore } from "../../store/twoFactorStore";
import { Button } from "../../components/atoms/Button";
import { Input } from "../../components/atoms/Input";
import { Card } from "../../components/molecules/Card";
import { Modal } from "../../components/molecules/Modal";
import { TwoFactorSetup } from "../../components/organisms/TwoFactorSetup";
import { BackupCodesDisplay } from "../../components/molecules/BackupCodesDisplay";
import {
  ShieldCheckIcon,
  ShieldExclamationIcon,
  KeyIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ArrowLeftIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";

interface PasswordFormData {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

export function Security() {
  const { t } = useTranslation("settings");
  const { t: tUsers } = useTranslation("users");
  const { t: tAuth } = useTranslation("auth");
  const navigate = useNavigate();
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [showBackupCodesModal, setShowBackupCodesModal] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const [disableError, setDisableError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { setTotpDisabledByAdmin, logout } = useAuthStore();
  const [passwordUpdating, setPasswordUpdating] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordFieldErrors },
    reset: resetPasswordForm,
    watch: watchPassword,
  } = useForm<PasswordFormData>({
    defaultValues: {
      current_password: "",
      new_password: "",
      confirm_password: "",
    },
  });
  const newPasswordValue = watchPassword("new_password");
  const { status, loading, fetchStatus, disable, regenerateBackupCodes, backupCodes } =
    useTwoFactorStore();

  useEffect(() => {
    fetchStatus().catch(() => {});
  }, [fetchStatus]);

  const handleDisable = async () => {
    if (disableCode.length !== 6) {
      setDisableError(t("disable_modal_code_required"));
      return;
    }

    setDisableError(null);
    try {
      await disable(disableCode);
      setShowDisableModal(false);
      setDisableCode("");
      // Refresh status after disable
      await fetchStatus();
      // Show success message with warning
      setSuccessMessage(t("security_2fa_disabled_success"));
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setDisableError(err instanceof Error ? err.message : t("error_disable_2fa"));
    }
  };

  const handleSetupComplete = () => {
    setTotpDisabledByAdmin(false); // User re-enabled 2FA (Story 6-6-9)
    fetchStatus().catch(() => {});
  };

  const handleRegenerateBackupCodes = async () => {
    try {
      await regenerateBackupCodes();
      setShowRegenerateModal(false);
      setShowBackupCodesModal(true);
    } catch (err) {
      // Error is handled by store
    }
  };

  const handleBackupCodesConfirmed = () => {
    setShowBackupCodesModal(false);
    setSuccessMessage(t("security_backup_codes_regenerated"));
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const onPasswordSubmit = async (data: PasswordFormData) => {
    setPasswordError(null);
    setPasswordUpdating(true);
    try {
      await profileService.changePassword({
        current_password: data.current_password,
        new_password: data.new_password,
        confirm_password: data.confirm_password,
      });
      resetPasswordForm();
      logout();
      navigate("/login", { replace: true, state: { passwordChanged: true } });
    } catch (err: unknown) {
      const message = getApiErrorMessage(err, t("profile_password_change_error"));
      setPasswordError(message);
    } finally {
      setPasswordUpdating(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto py-6 px-4">
      <Link
        to="/settings"
        className="inline-flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 mb-4"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        {t("security_back")}
      </Link>
      <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-6">
        {t("security_page_title")}
      </h1>

      {successMessage && (
        <div className="mb-4 rounded-lg bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 p-3 flex items-start gap-2">
          <CheckIcon className="w-5 h-5 text-success-600 dark:text-success-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-success-800 dark:text-success-200">{successMessage}</p>
        </div>
      )}

      <Card className="p-6 mb-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
            <LockClosedIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
              {t("security_password_section_title")}
            </h2>
            <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
              {t("security_password_section_desc")}
            </p>
          </div>
        </div>

        {passwordError && (
          <div className="mb-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3">
            <p className="text-sm text-red-800 dark:text-red-200">{passwordError}</p>
          </div>
        )}

        <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="space-y-4">
          <Input
            label={t("profile_current_password_label")}
            type={showCurrentPassword ? "text" : "password"}
            autoComplete="current-password"
            {...registerPassword("current_password", {
              required: t("profile_password_required"),
            })}
            error={passwordFieldErrors.current_password?.message}
            rightSlot={
              <button
                type="button"
                onClick={() => setShowCurrentPassword((v) => !v)}
                className="rounded p-0.5 text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                aria-label={
                  showCurrentPassword
                    ? tAuth("login_hide_password")
                    : tAuth("login_show_password")
                }
              >
                {showCurrentPassword ? (
                  <EyeSlashIcon className="h-5 w-5" aria-hidden />
                ) : (
                  <EyeIcon className="h-5 w-5" aria-hidden />
                )}
              </button>
            }
          />
          <Input
            label={t("profile_new_password_label")}
            type={showNewPassword ? "text" : "password"}
            autoComplete="new-password"
            {...registerPassword("new_password", {
              required: t("profile_password_required"),
              validate: (v) => {
                const r = validatePasswordPolicy(v, tUsers);
                return r === true ? true : r;
              },
            })}
            error={passwordFieldErrors.new_password?.message}
            rightSlot={
              <button
                type="button"
                onClick={() => setShowNewPassword((v) => !v)}
                className="rounded p-0.5 text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                aria-label={
                  showNewPassword ? tAuth("login_hide_password") : tAuth("login_show_password")
                }
              >
                {showNewPassword ? (
                  <EyeSlashIcon className="h-5 w-5" aria-hidden />
                ) : (
                  <EyeIcon className="h-5 w-5" aria-hidden />
                )}
              </button>
            }
          />
          <Input
            label={t("profile_confirm_password_label")}
            type={showConfirmPassword ? "text" : "password"}
            autoComplete="new-password"
            {...registerPassword("confirm_password", {
              required: t("profile_password_required"),
              validate: (v) => v === newPasswordValue || t("profile_password_mismatch"),
            })}
            error={passwordFieldErrors.confirm_password?.message}
            rightSlot={
              <button
                type="button"
                onClick={() => setShowConfirmPassword((v) => !v)}
                className="rounded p-0.5 text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                aria-label={
                  showConfirmPassword
                    ? tAuth("login_hide_password")
                    : tAuth("login_show_password")
                }
              >
                {showConfirmPassword ? (
                  <EyeSlashIcon className="h-5 w-5" aria-hidden />
                ) : (
                  <EyeIcon className="h-5 w-5" aria-hidden />
                )}
              </button>
            }
          />
          <Button
            type="submit"
            variant="primary"
            loading={passwordUpdating}
            disabled={passwordUpdating}
            className="w-full sm:w-auto"
          >
            {passwordUpdating ? t("profile_password_submitting") : t("profile_password_submit")}
          </Button>
        </form>
      </Card>

      <Card className="p-6">
        <div className="flex items-start gap-4">
          <div
            className={`
              w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0
              ${
                status?.enabled
                  ? "bg-success-100 dark:bg-success-900/30"
                  : "bg-warning-100 dark:bg-warning-900/30"
              }
            `}
          >
            {status?.enabled ? (
              <ShieldCheckIcon className="w-6 h-6 text-success-600 dark:text-success-400" />
            ) : (
              <ShieldExclamationIcon className="w-6 h-6 text-warning-600 dark:text-warning-400" />
            )}
          </div>

          <div className="flex-1">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
              {t("security_2fa_title")}
            </h2>
            <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
              {t("security_2fa_description")}
            </p>

            {loading && !status && (
              <div className="mt-4 animate-pulse h-10 bg-neutral-200 dark:bg-neutral-700 rounded" />
            )}

            {status && (
              <div className="mt-4 space-y-4">
                <div className="flex items-center gap-2">
                  <span
                    className={`
                      inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                      ${
                        status.enabled
                          ? "bg-success-100 text-neutral-900 dark:bg-success-700/40 dark:text-neutral-100"
                          : "bg-warning-100 text-neutral-900 dark:bg-warning-700/40 dark:text-neutral-100"
                      }
                    `}
                  >
                    {status.enabled ? t("security_2fa_enabled") : t("security_2fa_not_enabled")}
                  </span>

                  {status.enabled && (
                    <span className="text-sm text-neutral-500 dark:text-neutral-400 flex items-center gap-1">
                      <KeyIcon className="w-4 h-4" />
                      {t("security_backup_codes_remaining", { count: status.backupCodesRemaining })}
                    </span>
                  )}
                </div>

                <div className="flex gap-2">
                  {status.enabled ? (
                    <>
                      <Button variant="danger" onClick={() => setShowDisableModal(true)}>
                        {t("btn_disable_2fa")}
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => setShowRegenerateModal(true)}
                        className="flex items-center gap-2"
                      >
                        <ArrowPathIcon className="w-4 h-4" />
                        {t("btn_regenerate_backup_codes")}
                      </Button>
                    </>
                  ) : (
                    <Button variant="primary" onClick={() => setShowSetupModal(true)}>
                      {t("btn_enable_2fa")}
                    </Button>
                  )}
                </div>

                {!status.enabled &&
                  status.gracePeriodStarted &&
                  status.graceDaysRemaining !== null && (
                    <div className="p-3 bg-warning-50 dark:bg-warning-700/20 border border-warning-200 dark:border-warning-600 rounded-lg">
                      <p className="text-sm text-neutral-900 dark:text-neutral-100">
                        {t("security_grace_period", { days: status.graceDaysRemaining })}
                        {status.graceDaysRemaining === 0 && ` ${t("security_grace_expired")}`}
                      </p>
                    </div>
                  )}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* 2FA Setup Modal */}
      <TwoFactorSetup
        isOpen={showSetupModal}
        onClose={() => setShowSetupModal(false)}
        onComplete={handleSetupComplete}
      />

      {/* Disable 2FA Modal */}
      <Modal
        isOpen={showDisableModal}
        onClose={() => {
          setShowDisableModal(false);
          setDisableCode("");
          setDisableError(null);
        }}
        title={t("disable_modal_title")}
        size="sm"
      >
        <div className="space-y-4">
          <div className="p-3 bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg">
            <div className="flex items-start gap-2">
              <ExclamationTriangleIcon className="w-5 h-5 text-warning-600 dark:text-warning-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-warning-800 dark:text-warning-200">
                {t("disable_modal_warning")}
              </p>
            </div>
          </div>

          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            {t("disable_modal_confirm_text")}
          </p>

          <Input
            label={t("disable_modal_code_label")}
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={disableCode}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "").slice(0, 6);
              setDisableCode(val);
              setDisableError(null);
            }}
            error={disableError || undefined}
            placeholder={t("disable_modal_code_placeholder")}
            autoFocus
            className="text-center text-xl tracking-widest font-mono"
          />

          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setShowDisableModal(false);
                setDisableCode("");
                setDisableError(null);
              }}
              className="flex-1"
            >
              {t("btn_cancel")}
            </Button>
            <Button
              variant="danger"
              onClick={handleDisable}
              loading={loading}
              disabled={disableCode.length !== 6}
              className="flex-1"
            >
              {t("btn_disable")}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Regenerate Backup Codes Confirmation Modal */}
      <Modal
        isOpen={showRegenerateModal}
        onClose={() => setShowRegenerateModal(false)}
        title={t("regenerate_modal_title")}
        size="sm"
      >
        <div className="space-y-4">
          <div className="p-3 bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg">
            <div className="flex items-start gap-2">
              <ExclamationTriangleIcon className="w-5 h-5 text-warning-600 dark:text-warning-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-warning-800 dark:text-warning-200">
                {t("regenerate_modal_warning")}
              </p>
            </div>
          </div>

          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            {t("regenerate_modal_description")}
          </p>

          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => setShowRegenerateModal(false)}
              className="flex-1"
            >
              {t("btn_cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={handleRegenerateBackupCodes}
              loading={loading}
              className="flex-1"
            >
              {t("btn_regenerate")}
            </Button>
          </div>
        </div>
      </Modal>

      {/* New Backup Codes Display Modal */}
      <Modal
        isOpen={showBackupCodesModal}
        onClose={() => {
          setShowBackupCodesModal(false);
          handleBackupCodesConfirmed();
        }}
        title={t("new_backup_codes_title")}
        size="md"
      >
        <BackupCodesDisplay codes={backupCodes} onConfirm={handleBackupCodesConfirmed} />
      </Modal>
    </div>
  );
}
